/**
 * Read-only preview of a question bank item, the way a learner sees it, plus the answer key.
 *
 *   <QuestionPreview question={q} showAnswers />
 *
 * Used by the Question bank "Preview" pane and anywhere a question needs to be shown without the
 * editor (exam builder, reports). Question text / explanation are rich HTML (RichHtml).
 */
import React from 'react';
import RichHtml from '../richtext/RichText';
import { StatusBadge } from '../datagrid/DataGrid';
import { CheckCircle, Circle, Square, CheckSquare, Lightbulb, Info } from '@phosphor-icons/react';

const TYPE_LABELS = {
  MCQ_SINGLE: 'Multiple choice (single)', MCQ_MULTI: 'Multiple choice (multi)', TRUE_FALSE: 'True / False',
  FILL_BLANK: 'Fill in the blank', SHORT_ANSWER: 'Short answer', ESSAY: 'Essay', NUMERIC: 'Numeric',
  MATCH: 'Matching', ORDER: 'Ordering', FILE_UPLOAD: 'File upload', RATING: 'Rating', TEXT_FEEDBACK: 'Text feedback',
};
export const typeLabel = (t) => TYPE_LABELS[t] || t;

function Choice({ option, index, multi, showAnswers }) {
  const letter = String.fromCharCode(65 + index);
  const correct = !!option.is_correct;
  const Icon = multi ? (showAnswers && correct ? CheckSquare : Square) : (showAnswers && correct ? CheckCircle : Circle);
  return (
    <li className={`flex items-start gap-2.5 rounded border px-3 py-2 text-[13px] ${showAnswers && correct ? 'border-emerald-300 bg-emerald-50' : 'border-border bg-white'}`} data-testid={`preview-option-${index}`}>
      <Icon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${showAnswers && correct ? 'text-emerald-600' : 'text-muted-foreground'}`} weight={showAnswers && correct ? 'fill' : 'regular'} />
      <span className="font-mono text-xs text-muted-foreground mt-0.5 w-4">{letter}.</span>
      <RichHtml html={option.text} as="span" className="flex-1 min-w-0" />
      {showAnswers && correct && <span className="text-[11px] font-semibold text-emerald-700 uppercase tracking-wide">Correct</span>}
    </li>
  );
}

function Meta({ label, children }) {
  return (
    <div className="min-w-0">
      <dt className="text-[11px] text-muted-foreground">{label}</dt>
      <dd className="text-[13px] mt-0.5 truncate">{children ?? '—'}</dd>
    </div>
  );
}

export default function QuestionPreview({ question: q, showAnswers = true, courseName, categoryName, index, total, compact = false }) {
  if (!q) return null;
  const type = q.question_type;
  const isChoice = ['MCQ_SINGLE', 'MCQ_MULTI', 'TRUE_FALSE'].includes(type);
  const multi = type === 'MCQ_MULTI';
  const options = q.options || [];
  return (
    <div className="space-y-4" data-testid="question-preview">
      {/* Learner view */}
      <div className="rounded-lg border border-border bg-white">
        {!compact && (
        <div className="flex items-center gap-2 px-3 h-9 border-b border-border bg-muted/30 text-xs text-muted-foreground">
          <span className="font-medium text-foreground">{typeof index === 'number' ? `Question ${index + 1}${total ? ` of ${total}` : ''}` : 'Question'}</span>
          <span>·</span><span>{typeLabel(type)}</span>
          <span className="ml-auto">{q.points ?? 1} mark{q.points === 1 ? '' : 's'}{q.negative_marks ? ` · −${q.negative_marks} wrong` : ''}</span>
        </div>
        )}
        <div className="p-4 space-y-3">
          <RichHtml html={q.question_text} className="text-[15px] font-medium leading-relaxed" data-testid="preview-question-text" />
          {q.media_url && <img src={q.media_url} alt="Figure" className="max-h-64 max-w-full rounded border border-border bg-white object-contain" />}

          {isChoice && (
            <ol className="space-y-1.5">
              {options.map((o, i) => <Choice key={o.id || i} option={o} index={i} multi={multi} showAnswers={showAnswers} />)}
            </ol>
          )}
          {type === 'MATCH' && (
            <table className="text-[13px] w-full">
              <tbody>
                {options.map((o, i) => (
                  <tr key={o.id || i} className="border-b border-border last:border-0">
                    <td className="py-1.5 pr-3"><RichHtml html={o.text} as="span" /></td>
                    <td className="py-1.5 text-muted-foreground w-6 text-center">→</td>
                    <td className="py-1.5">{showAnswers ? <span className="text-emerald-700">{o.match_target || '—'}</span> : <span className="inline-block h-6 w-32 rounded border border-dashed border-border" />}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {type === 'ORDER' && (
            <ol className="space-y-1.5">
              {(showAnswers ? [...options].sort((a, b) => (a.order_position ?? 0) - (b.order_position ?? 0)) : options).map((o, i) => (
                <li key={o.id || i} className="flex items-center gap-2 rounded border border-border px-3 py-1.5 text-[13px]"><span className="font-mono text-xs text-muted-foreground">{i + 1}.</span><RichHtml html={o.text} as="span" /></li>
              ))}
            </ol>
          )}
          {['FILL_BLANK', 'SHORT_ANSWER', 'NUMERIC', 'ESSAY', 'TEXT_FEEDBACK'].includes(type) && (
            <div className={`rounded border border-dashed border-border bg-muted/20 px-3 text-[13px] text-muted-foreground flex items-center ${type === 'ESSAY' || type === 'TEXT_FEEDBACK' ? 'h-20 items-start pt-2' : 'h-8'}`}>
              {type === 'NUMERIC' ? 'Learner enters a number' : type === 'ESSAY' || type === 'TEXT_FEEDBACK' ? 'Learner writes a longer answer' : 'Learner types the answer'}
            </div>
          )}
          {type === 'FILE_UPLOAD' && <div className="rounded border border-dashed border-border bg-muted/20 px-3 h-12 text-[13px] text-muted-foreground flex items-center">Learner uploads a file</div>}
          {type === 'RATING' && <div className="flex gap-1 text-muted-foreground">{[1, 2, 3, 4, 5].map((n) => <span key={n} className="w-7 h-7 rounded border border-border flex items-center justify-center text-xs">{n}</span>)}</div>}

          {q.hint && (
            <div className="flex items-start gap-2 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
              <Lightbulb className="w-4 h-4 flex-shrink-0 mt-0.5" weight="fill" /><span><span className="font-medium">Hint: </span>{q.hint}</span>
            </div>
          )}
        </div>
      </div>

      {/* Answer key */}
      {showAnswers && (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50/60" data-testid="preview-answer-key">
          <div className="flex items-center gap-2 px-3 h-9 border-b border-emerald-200 text-xs font-medium text-emerald-900">
            <CheckCircle className="w-4 h-4" weight="fill" /> Answer key
          </div>
          <div className="p-3 space-y-2 text-[13px]">
            {isChoice && (
              <div>
                <span className="text-muted-foreground">Correct: </span>
                {options.map((o, i) => (o.is_correct ? String.fromCharCode(65 + i) : null)).filter(Boolean).join(', ') || '—'}
              </div>
            )}
            {['FILL_BLANK', 'SHORT_ANSWER'].includes(type) && (
              <div><span className="text-muted-foreground">Accepted: </span>{[q.correct_answer, ...(q.correct_answers || [])].filter(Boolean).join(' · ') || '—'}</div>
            )}
            {type === 'NUMERIC' && (
              <div><span className="text-muted-foreground">Answer: </span>{q.numeric_answer ?? q.correct_answer ?? '—'}{q.numeric_tolerance ? ` (± ${q.numeric_tolerance})` : ''}</div>
            )}
            {['ESSAY', 'FILE_UPLOAD', 'TEXT_FEEDBACK', 'RATING'].includes(type) && <div className="text-muted-foreground">Graded manually / no answer key.</div>}
            {q.explanation && (
              <div className="flex items-start gap-2 pt-1 border-t border-emerald-200/70">
                <Info className="w-4 h-4 flex-shrink-0 mt-0.5 text-emerald-700" />
                <RichHtml html={q.explanation} className="flex-1 min-w-0" />
              </div>
            )}
          </div>
        </div>
      )}

      {/* Facts */}
      {!compact && (
      <dl className="grid grid-cols-2 gap-x-4 gap-y-2 rounded-lg border border-border bg-white p-3" data-testid="preview-facts">
        <Meta label="Difficulty"><StatusBadge value={q.difficulty || 'medium'} /></Meta>
        <Meta label="Status"><StatusBadge value={q.is_active === false ? 'inactive' : 'active'} /></Meta>
        <Meta label="Topic">{q.topic}</Meta>
        <Meta label="Category">{categoryName}</Meta>
        <Meta label="Course">{courseName}</Meta>
        <Meta label="Used in exams">{q.usage_count ?? 0}</Meta>
        {q.tags?.length > 0 && <Meta label="Tags">{q.tags.join(', ')}</Meta>}
        {q.time_limit_seconds ? <Meta label="Time limit">{q.time_limit_seconds}s</Meta> : null}
      </dl>
      )}
    </div>
  );
}
